import React from 'react';
import { Table, Tag, InputNumber, Button } from 'antd';

interface PriceEntry {
  qty: number | null;
  ordered: number;
  price: number;
  warehouse?: string | null;
}

interface OriginPrices {
  origin: string | null;
  prices: { [supplierId: number]: PriceEntry };
}

interface SupplierRow {
  rowKey: string;
  supplierId: number;
  supplierName: string;
  origin: string | null;
  warehouse: string | null;
  qty: number | null;
  ordered: number;
  price: number;
}

interface Props {
  sizeId: number;
  containerSize: string;
  allOriginPrices: OriginPrices[];
  suppliers: { id: number; name: string }[];
  getQty: (sizeId: number, supplierId: number, origin: string | null) => number;
  setQty: (sizeId: number, supplierId: number, origin: string | null, qty: number) => void;
  onAllZero: () => void;
  brand?: string;
  productType?: string;
  modelName?: string;
  columnOrder?: string[];
}

const WAREHOUSE_LABELS: Record<string, string> = {
  мурманск: 'Склад Мурм.',
  спб: 'Склад СПб',
  общий: 'Склад общий',
  заказ: 'Заказ',
};

const parseContainerVolume = (cs: string): number => {
  const m = cs.match(/(\d+(?:[.,]\d+)?)\s*(?:л|кг|L)/i);
  if (!m) return 0;
  return parseFloat(m[1].replace(',', '.'));
};

const getStepButtons = (cs: string): number[] => {
  const vol = parseContainerVolume(cs);
  if (vol <= 1) return [12, -12, 0];
  if (vol <= 5) return [4, -4, 0];
  return [1, -1, 0];
};

const OrderSubTable: React.FC<Props> = ({ sizeId, containerSize, allOriginPrices, suppliers, getQty, setQty, onAllZero, brand, productType, modelName, columnOrder }) => {
  const steps = getStepButtons(containerSize);

  // Product info label in column order
  const infoFields: Record<string, string | undefined> = { brand, type: productType, model: modelName, tara: containerSize };
  const fieldOrder = columnOrder
    ? columnOrder.filter(k => !k.startsWith('supplier:'))
    : ['brand', 'type', 'model', 'tara'];
  const infoLabel = fieldOrder.map(k => infoFields[k]).filter(Boolean).join('  ');

  // Строки: для каждой (origin, supplier) где есть цена, отсортированные по цене
  const rows: SupplierRow[] = allOriginPrices
    .flatMap(({ origin, prices }) =>
      suppliers
        .filter(s => prices[s.id] != null)
        .map(s => ({
          rowKey: `${origin ?? '__null__'}::${s.id}`,
          supplierId: s.id,
          supplierName: s.name,
          origin,
          warehouse: prices[s.id].warehouse ?? null,
          qty: prices[s.id].qty,
          ordered: prices[s.id].ordered,
          price: prices[s.id].price,
        }))
    )
    .sort((a, b) => a.price - b.price);

  const columns = [
    {
      title: 'Страна',
      key: 'origin',
      width: 64,
      align: 'center' as const,
      render: (_: any, row: SupplierRow) => {
        if (!row.origin) return null;
        const color = row.origin === 'EU' ? 'blue' : row.origin === 'TR' ? 'orange' : 'default';
        return <Tag color={color} style={{ margin: 0, fontSize: 11, padding: '0 4px' }}>{row.origin}</Tag>;
      },
    },
    {
      title: 'Поставщик',
      key: 'supplier',
      render: (_: any, row: SupplierRow) => (
        <span style={{ fontSize: 12 }}>{row.supplierName}</span>
      ),
    },
    {
      title: 'Наличие',
      key: 'availability',
      width: 110,
      render: (_: any, row: SupplierRow) => {
        const inCart = getQty(sizeId, row.supplierId, row.origin);
        if (row.warehouse) {
          const label = WAREHOUSE_LABELS[row.warehouse] || row.warehouse;
          const taken = row.ordered + inCart;
          if (taken > 0) return <span style={{ fontSize: 12 }}>{label} (−{taken})</span>;
          return <span style={{ fontSize: 12 }}>{label}</span>;
        }
        if (row.qty == null) {
          if (row.ordered > 0 || inCart > 0) return <span style={{ fontSize: 12 }}>? (−{row.ordered + inCart})</span>;
          return <span style={{ fontSize: 12 }}>?</span>;
        }
        const remaining = row.qty - row.ordered - inCart;
        if (row.ordered > 0 || inCart > 0) {
          if (remaining < 0) return <span style={{ color: '#ff4d4f', fontWeight: 600, fontSize: 12 }}>⚠ {remaining}/{row.qty}</span>;
          return <span style={{ fontSize: 12 }}>{remaining}/{row.qty}</span>;
        }
        return <span style={{ fontSize: 12 }}>{row.qty}</span>;
      },
    },
    {
      title: 'Кол-во',
      key: 'qty_input',
      width: 120,
      render: (_: any, row: SupplierRow) => {
        const curQty = getQty(sizeId, row.supplierId, row.origin);
        const handleChange = (newQty: number, isClear?: boolean) => {
          setQty(sizeId, row.supplierId, row.origin, newQty);
          if (isClear) {
            const allZero = rows.every(r => {
              const q = r.rowKey === row.rowKey ? newQty : getQty(sizeId, r.supplierId, r.origin);
              return q === 0;
            });
            if (allZero) onAllZero();
          }
        };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <InputNumber
              size="small"
              min={0}
              value={curQty}
              style={{ width: 60 }}
              onChange={(v) => handleChange(v ?? 0)}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {steps.map((step, i) => {
                const label = step === 0 ? '✕' : step > 0 ? `+${step}` : `${step}`;
                const color = step === 0 ? '#595959' : step > 0 ? '#52c41a' : '#8c8c8c';
                return (
                  <Button
                    key={i}
                    size="small"
                    type="text"
                    style={{
                      fontSize: 11,
                      height: 16,
                      minWidth: 28,
                      padding: '0 3px',
                      lineHeight: '14px',
                      color,
                    }}
                    onClick={() => {
                      if (step === 0) {
                        handleChange(0, true);
                      } else {
                        handleChange(Math.max(0, curQty + step));
                      }
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Цена / Сумма',
      key: 'price_sum',
      width: 120,
      align: 'right' as const,
      render: (_: any, row: SupplierRow) => {
        const curQty = getQty(sizeId, row.supplierId, row.origin);
        const sum = curQty * row.price;
        return (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {row.price.toLocaleString('ru')} ₽
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: sum > 0 ? '#1677ff' : '#8c8c8c' }}>
              {sum > 0 ? `${sum.toLocaleString('ru', { minimumFractionDigits: 2 })} ₽` : ''}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{
      border: '1px solid #bdd7ff',
      borderRadius: 6,
      margin: '2px 0',
      boxShadow: '0 2px 10px rgba(22,119,255,0.12), 0 1px 4px rgba(0,0,0,0.05)',
    }}>
      {infoLabel && (
        <div style={{ padding: '4px 8px 2px', fontSize: 11, color: '#262626', textAlign: 'left' }}>{infoLabel}</div>
      )}
      <Table
        size="small"
        bordered
        dataSource={rows}
        columns={columns}
        pagination={false}
        rowKey="rowKey"
        rowClassName={(row) => {
          const curQty = getQty(sizeId, row.supplierId, row.origin);
          return curQty > 0 ? 'oil-subtable-active' : '';
        }}
      />
    </div>
  );
};

export default OrderSubTable;
